/**
 * ServiceOrderInsightsPanel - Subaba Indicadores do Resumo da O.S. (BR-OS-055).
 *
 * Cartões de tempo/qualidade/custo, linha do tempo por situação e itens não realizados.
 * Dados só da própria O.S.; etapa sem registro mostra "Não registrado".
 * A lista de remessas fica na aba própria "Remessas" do modal (não se repete aqui).
 *
 * RELEASE: 2026-10-18 v1.1.0 - Remove a seção "Remessas" (duplicava a aba própria do modal);
 *            o cartão "Custo de remessa" permanece.
 * RELEASE: 2026-10-18 v1.0.0 - Versão inicial.
 */
import { useMemo } from 'react';
import { DashboardMetricCard } from '@/components/dashboard/shared/DashboardMetricCard';
import { DetailsContentCard } from '@/components/shared/details-modal/DetailsModalPrimitives';
import { InlineNotice } from '@/components/ui/inline-notice';
import { Skeleton } from '@/components/ui/skeleton';
import { useServiceOrderInsights } from '@/hooks/useServiceOrderInsights';
import { ServiceOrderStatusGantt } from './ServiceOrderStatusGantt';
import { computeServiceOrderInsights, formatInsightDuration, punctualityLabel, withTravelSegment } from '@/lib/serviceOrders/insights';

const brl = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

export function ServiceOrderInsightsPanel({ orderId }: { orderId: string }) {
  const query = useServiceOrderInsights(orderId);
  const ins = useMemo(() => (query.data ? computeServiceOrderInsights(query.data) : null), [query.data]);

  if (query.isLoading) return <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-20" />)}</div>;
  if (query.isError || !ins || !query.data) return <InlineNotice variant="error">Não foi possível carregar os indicadores.</InlineNotice>;

  const execVsEst = ins.executionSeconds !== null && ins.estimatedSeconds
    ? `Estimado: ${formatInsightDuration(ins.estimatedSeconds)}` : ins.estimatedSeconds ? `Estimado: ${formatInsightDuration(ins.estimatedSeconds)}` : undefined;
  const freightValue = ins.shipmentsCount === 0 ? 'Sem remessa' : ins.freightTotal === null ? 'Frete não informado' : brl(ins.freightTotal);

  return (
    <div className="space-y-4">
      {/* Cartões de tempo, qualidade e custo */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <DashboardMetricCard title="Tempo total" value={formatInsightDuration(ins.totalSeconds)} subtitle="Abertura até conclusão" />
        <DashboardMetricCard title="Até o atendimento" value={formatInsightDuration(ins.timeToArrivalSeconds)} subtitle="Abertura até chegada" />
        <DashboardMetricCard title="Pontualidade" value={punctualityLabel(ins.delaySeconds)} subtitle="Chegada x início agendado" />
        <DashboardMetricCard title="Deslocamento" value={formatInsightDuration(ins.travelSeconds)} subtitle="Saída até chegada" />
        <DashboardMetricCard title="Execução" value={formatInsightDuration(ins.executionSeconds)} subtitle={execVsEst} tooltip="Da chegada ao envio para revisão, descontando pausas." />
        <DashboardMetricCard title="Serviços realizados" value={`${ins.servicesDone} de ${ins.servicesTotal}`} subtitle={ins.fieldAdded ? `${ins.fieldAdded} adicional(is) em campo` : 'Sem adicionais'} />
        <DashboardMetricCard title="Retrabalho" value={ins.reworkCount === 0 ? 'Nenhum' : `${ins.reworkCount}x em correção`} subtitle={`Checklists aprovados: ${ins.checklistsApproved} de ${ins.checklistsTotal}`} />
        <DashboardMetricCard title="Custo de remessa" value={freightValue}
          subtitle={ins.shipmentsCount ? `${ins.shipmentsCount} remessa(s)${ins.freightMissing ? ` · ${ins.freightMissing} sem frete` : ''}` : undefined}
          tooltip="Soma do frete das remessas ligadas à O.S., incluindo devoluções. O valor pago ao prestador ainda não é registrado." />
      </div>

      {/* Linha do tempo por situação */}
      {/* v1.1.0: Gantt com eixo real, grupos, marcos e resumo */}
      <DetailsContentCard title="Linha do tempo da O.S.">
        {/* v1.2.0: deslocamento vira trecho próprio (início → check-in); marco losango removido */}
        <ServiceOrderStatusGantt segments={withTravelSegment(ins.segments,
            query.data.events.find(e => e.type === 'provider_travel_started')?.at ?? null,
            query.data.events.find(e => e.type === 'provider_check_in')?.at ?? null)} start={query.data.created_at}
          end={ins.segments[ins.segments.length - 1]?.end ?? new Date().toISOString()}
          markers={[
            { label: 'Agendado', at: query.data.scheduled_start },
            { label: 'Check-in', at: query.data.events.find(e => e.type === 'provider_check_in')?.at ?? null },
          ]} />
      </DetailsContentCard>

      {/* Serviços não realizados */}
      {ins.notDone.length > 0 && (
        <DetailsContentCard title="Serviços não realizados">
          <ul className="space-y-1.5 text-sm">
            {ins.notDone.map((n, i) => (
              <li key={i}><span className="font-medium">{n.name}</span>{n.reason ? <span className="text-muted-foreground"> — {n.reason}</span> : null}</li>
            ))}
          </ul>
        </DetailsContentCard>
      )}
    </div>
  );
}
